import Icon from 'ol/style/Icon';

/**
 * Cartographic point symbols drawn in code onto a canvas (no image files, no network, no CORS): they work offline in
 * the PWA, keep the PNG export exportable and are redrawn at the export render scale, so they stay sharp and keep
 * their proportions. Every symbol is anchored at its centre, exactly on the point.
 *
 * Memory is bounded: a drawing is cached by symbol, colour, variant and size only (opacity is applied when drawing,
 * so the opacity slider never creates canvases), in a least-recently-used cache with a fixed ceiling; the icons that
 * wrap those drawings are cached the same way. Sweeping the size, opacity and colour controls therefore cannot make
 * the session grow without limit, and a map with many symbols keeps hitting the cache while panning and zooming.
 * (OpenLayers keeps its own shared icon cache, bounded at 1 024 entries and expired on render.)
 */

/** Drawn at twice the size and shown at half: crisp on high-density screens. */
const PIXEL_RATIO = 2;
/** Ceilings: far above the distinct symbols a project draws at once, far below what a slider sweep would accumulate. */
export const SYMBOL_CACHE_LIMITS = { canvases: 160, icons: 320 } as const;

/** Map in insertion order used as an LRU: a hit moves the entry to the end, overflow drops from the start. */
class Lru<V> {
  private readonly entries = new Map<string, V>();
  constructor(private readonly limit: number) {}
  get size(): number { return this.entries.size; }
  has(key: string): boolean { return this.entries.has(key); }
  get(key: string): V | undefined {
    if (!this.entries.has(key)) return undefined;
    const value = this.entries.get(key)!; this.entries.delete(key); this.entries.set(key, value); return value;
  }
  set(key: string, value: V): void {
    this.entries.delete(key); this.entries.set(key, value);
    while (this.entries.size > this.limit) this.entries.delete(this.entries.keys().next().value!);
  }
  values(): IterableIterator<V> { return this.entries.values(); }
  clear(): void { this.entries.clear(); }
}

const canvases = new Lru<HTMLCanvasElement | null>(SYMBOL_CACHE_LIMITS.canvases);
const icons = new Lru<Icon | null>(SYMBOL_CACHE_LIMITS.icons);

/** Sizes differing below 1/100 draw the same pixels: one drawing for both. */
const sizeKey = (size: number): string => size.toFixed(2);

function drawing(key: string, width: number, height: number, size: number, draw: (context: CanvasRenderingContext2D, u: (value: number) => number) => void): HTMLCanvasElement | null {
  const cacheKey = `${key}|${sizeKey(size)}`;
  if (canvases.has(cacheKey)) return canvases.get(cacheKey)!;
  let canvas: HTMLCanvasElement | null = null;
  try {
    const unit = Number(sizeKey(size)) * PIXEL_RATIO;
    const element = document.createElement('canvas');
    element.width = Math.max(1, Math.ceil(width * unit)); element.height = Math.max(1, Math.ceil(height * unit));
    const context = element.getContext('2d');
    if (context && typeof context.roundRect === 'function') {
      context.save(); draw(context, (value) => value * unit); context.restore();
      canvas = element;
    }
  } catch { canvas = null; }
  canvases.set(cacheKey, canvas);
  return canvas;
}

/**
 * OpenLayers icon of a `width` × `height` drawing (CSS pixels at size 1) shown at `size` (symbol scale × render
 * scale). `draw` paints in logical units through `u`. Null where no 2D canvas exists (tests): callers fall back.
 */
export function canvasIcon(key: string, width: number, height: number, size: number, opacity: number, draw: (context: CanvasRenderingContext2D, u: (value: number) => number) => void, anchor: readonly [number, number] = [0.5, 0.5]): Icon | null {
  const cacheKey = `${key}|${sizeKey(size)}|${opacity}|${anchor[0].toFixed(3)},${anchor[1].toFixed(3)}`;
  if (icons.has(cacheKey)) return icons.get(cacheKey)!;
  const canvas = drawing(key, width, height, size, draw);
  const icon = canvas ? new Icon({ img: canvas, scale: 1 / PIXEL_RATIO, opacity, declutterMode: 'obstacle', anchor: [anchor[0], anchor[1]] }) : null;
  icons.set(cacheKey, icon);
  return icon;
}

/** Current cache occupation (QA and tests): entries and the pixel memory of the cached drawings. */
export function symbolCacheStats(): { canvases: number; icons: number; canvasBytes: number } {
  let canvasBytes = 0;
  for (const canvas of canvases.values()) if (canvas) canvasBytes += canvas.width * canvas.height * 4;
  return { canvases: canvases.size, icons: icons.size, canvasBytes };
}

/** Empties both caches (tests). */
export function clearSymbolCache(): void { canvases.clear(); icons.clear(); }

/** Light outline drawn under a symbol's silhouette: legible on satellite imagery without a plate or a box. */
export const HALO = 'rgba(255,255,255,0.92)';
